import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import NonSpeakingPanel from "@/components/NonSpeakingPanel";
import { ChevronLeft, ChevronRight, Play, Pause, RotateCcw, X, Maximize2, WifiOff, Clock } from "lucide-react";

const CACHE_KEY = (id) => `im_class_cache_${id}`;

function fmt(sec) {
  const m = Math.floor(Math.abs(sec) / 60);
  const s = Math.abs(sec) % 60;
  return `${sec < 0 ? "-" : ""}${m}:${String(s).padStart(2, "0")}`;
}

export default function ClassRunner() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [cls, setCls] = useState(null);
  const [idx, setIdx] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [running, setRunning] = useState(false);
  const [projector, setProjector] = useState(false);
  const [offline, setOffline] = useState(false);
  const tick = useRef(null);

  // Load with offline cache fallback
  useEffect(() => {
    api.get(`/classes/${classId}`).then(({ data }) => {
      setCls(data);
      localStorage.setItem(CACHE_KEY(classId), JSON.stringify(data));
    }).catch(() => {
      const cached = localStorage.getItem(CACHE_KEY(classId));
      if (cached) { setCls(JSON.parse(cached)); setOffline(true); }
    });
  }, [classId]);

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    setOffline(!navigator.onLine);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  const block = cls?.blocks?.[idx];

  useEffect(() => {
    if (block) { setRemaining((block.duration_min || 0) * 60); setRunning(false); }
  }, [idx, cls]);

  useEffect(() => {
    if (running) {
      tick.current = setInterval(() => setRemaining((r) => r - 1), 1000);
    } else if (tick.current) {
      clearInterval(tick.current);
    }
    return () => tick.current && clearInterval(tick.current);
  }, [running]);

  const next = useCallback(() => setIdx((i) => Math.min(i + 1, (cls?.blocks?.length || 1) - 1)), [cls]);
  const prev = useCallback(() => setIdx((i) => Math.max(i - 1, 0)), []);

  const handleKey = useCallback((e) => {
    if (e.key === " " || e.key.toLowerCase() === "n") { e.preventDefault(); next(); }
    else if (e.key.toLowerCase() === "p") prev();
    else if (e.key.toLowerCase() === "t") setRunning((r) => !r);
    else if (e.key.toLowerCase() === "f") setProjector((p) => !p);
    else if (e.key === "Escape") { if (projector) setProjector(false); else navigate("/director/agenda"); }
  }, [next, prev, projector, navigate]);

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  if (!cls) return <div className="h-screen bg-[#0c0809] text-white flex items-center justify-center font-runner text-2xl">Loading agenda…</div>;

  const isIfTime = block?.type === "If Time";
  const game = block?.game;
  const timeUp = remaining <= 0;
  const total = cls.blocks.length;

  // Projector full-bleed view
  if (projector) {
    return (
      <div className="h-screen w-screen stage-scrim text-white flex flex-col p-8 lg:p-14" data-testid="projector-view">
        <div className="flex items-center justify-between">
          <span className="font-runner text-2xl lg:text-3xl uppercase tracking-wide text-[#bf6163]">
            {block.type} {isIfTime && "· Optional"}
          </span>
          <span className={`font-timer text-6xl lg:text-8xl font-black ${timeUp ? "text-[#e05357]" : "text-white"}`} data-testid="projector-timer">
            {fmt(remaining)}
          </span>
        </div>
        <div className="flex-1 flex flex-col justify-center">
          <h1 className="font-runner text-5xl lg:text-8xl font-black leading-none tracking-tight" data-testid="projector-title">
            {game?.name || block.title}
          </h1>
          {game?.description && <p className="mt-8 text-2xl lg:text-4xl text-[#ffdedf] font-semibold max-w-5xl leading-snug">{game.description}</p>}
          {!game && block.content && <p className="mt-8 text-2xl lg:text-4xl text-[#dfd8da] max-w-5xl">{block.content}</p>}
        </div>
        <div className="flex items-center justify-between text-[#dfd8da]">
          <span className="font-runner text-xl">Block {idx + 1} / {total}</span>
          <span className="font-runner text-xl">Press F to exit projector · Space to advance</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen overflow-hidden flex flex-col bg-[#0c0809] text-white" data-testid="class-runner">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-[#3c2225] bg-[#140e0f]">
        <button onClick={() => navigate("/director/agenda")} data-testid="runner-exit" className="flex items-center gap-1 text-sm font-semibold text-[#dfd8da] hover:text-white">
          <X size={18} /> Exit
        </button>
        <div className="text-center">
          <div className="font-runner text-lg uppercase tracking-wide text-[#bf6163]">Level {cls.level} · S{cls.session_no} · C{cls.class_no}</div>
          <div className="text-xs text-[#545454]">{cls.title}</div>
        </div>
        <div className="flex items-center gap-3">
          {offline && <span className="flex items-center gap-1 text-xs text-[#fbbf24]" data-testid="offline-badge"><WifiOff size={14} /> Offline · cached</span>}
          <button onClick={() => setProjector(true)} data-testid="runner-projector" className="flex items-center gap-1 text-sm font-semibold bg-[#920e12] px-3 py-1.5 rounded-lg hover:bg-[#bf6163]">
            <Maximize2 size={15} /> Projector
          </button>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-4 p-4 md:p-6 overflow-hidden">
        {/* Stage */}
        <div className="lg:col-span-8 flex flex-col rounded-2xl bg-[#170f11] border-2 border-[#550001] p-6 lg:p-10 overflow-y-auto">
          <div className="flex items-center justify-between">
            <span className={`font-runner text-2xl uppercase tracking-wide ${isIfTime ? "text-[#fbbf24]" : "text-[#bf6163]"}`} data-testid="runner-block-type">
              {block.type} {isIfTime && "· Optional (If Time)"}
            </span>
            <span className="font-runner text-lg text-[#dfd8da]">{block.duration_min} min</span>
          </div>
          <h1 className="mt-4 font-runner text-4xl lg:text-6xl font-black leading-none tracking-tight" data-testid="runner-title">
            {game?.name || block.title}
          </h1>
          {game ? (
            <div className="mt-6 space-y-5">
              <div>
                <h2 className="text-xs uppercase tracking-widest font-extrabold text-[#bf6163]">Description</h2>
                <p className="mt-1 text-xl text-[#dfd8da] leading-relaxed">{game.description}</p>
              </div>
              <div>
                <h2 className="text-xs uppercase tracking-widest font-extrabold text-[#bf6163]">Example</h2>
                <p className="mt-1 text-lg text-[#dfd8da] italic leading-relaxed">{game.example}</p>
              </div>
              {game.skill_sets?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {game.skill_sets.map((s) => <span key={s} className="text-sm px-3 py-1 rounded-full bg-[#3c2225] text-[#ffdedf] font-semibold">{s}</span>)}
                </div>
              )}
              <NonSpeakingPanel text={game.non_speaking_modification} dark />
            </div>
          ) : (
            <p className="mt-6 text-xl text-[#dfd8da] leading-relaxed">{block.content}</p>
          )}
        </div>

        {/* Tools */}
        <div className="lg:col-span-4 flex flex-col gap-4 overflow-y-auto">
          {/* Timer */}
          <div className="rounded-2xl bg-[#170f11] border-2 border-[#3c2225] p-6 text-center">
            <div className="flex items-center justify-center gap-2 text-xs uppercase tracking-widest font-extrabold text-[#bf6163]"><Clock size={14} /> Timer</div>
            <div className={`mt-2 font-timer text-6xl font-black ${timeUp ? "text-[#e05357]" : "text-white"}`} data-testid="runner-timer">{fmt(remaining)}</div>
            <div className="mt-4 flex justify-center gap-2">
              <button onClick={() => setRunning((r) => !r)} data-testid="runner-timer-toggle" className="flex items-center gap-1 px-4 py-2 rounded-lg bg-[#920e12] hover:bg-[#bf6163] font-semibold">
                {running ? <><Pause size={16} /> Pause</> : <><Play size={16} /> Start</>}
              </button>
              <button onClick={() => setRemaining((block.duration_min || 0) * 60)} data-testid="runner-timer-reset" className="px-3 py-2 rounded-lg border border-[#3c2225] hover:bg-[#3c2225]">
                <RotateCcw size={16} />
              </button>
            </div>
          </div>

          {/* Agenda outline */}
          <div className="rounded-2xl bg-[#170f11] border-2 border-[#3c2225] p-4 flex-1">
            <div className="text-xs uppercase tracking-widest font-extrabold text-[#bf6163] mb-2 px-2">Agenda · {total} blocks</div>
            <ol className="space-y-1">
              {cls.blocks.map((b, i) => (
                <li key={i}>
                  <button
                    onClick={() => setIdx(i)}
                    data-testid={`runner-jump-${i}`}
                    className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between transition-colors duration-150 ${i === idx ? "bg-[#920e12] text-white" : "text-[#dfd8da] hover:bg-[#3c2225]"}`}
                  >
                    <span className="truncate text-sm font-semibold">{i + 1}. {b.game?.name || b.title}</span>
                    <span className={`text-xs ml-2 shrink-0 ${b.type === "If Time" ? "text-[#fbbf24]" : "opacity-60"}`}>{b.type === "If Time" ? "opt" : `${b.duration_min}m`}</span>
                  </button>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      {/* Bottom nav */}
      <div className="flex items-center justify-between px-4 md:px-6 py-4 border-t border-[#3c2225] bg-[#140e0f]">
        <button onClick={prev} disabled={idx === 0} data-testid="runner-prev" className="flex items-center gap-1 px-5 py-3 rounded-xl border border-[#3c2225] font-bold disabled:opacity-30 hover:bg-[#3c2225]">
          <ChevronLeft size={20} /> Prev
        </button>
        <span className="font-runner text-xl text-[#dfd8da]">Block {idx + 1} / {total}</span>
        <button onClick={next} disabled={idx === total - 1} data-testid="runner-next" className="flex items-center gap-1 px-7 py-3 rounded-xl bg-[#920e12] hover:bg-[#bf6163] font-bold disabled:opacity-30">
          Next <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
}
